import * as types from "../types";
import apiService from "../../services/api";
import router from "../../router";

import { Commit, Dispatch } from "vuex";

let storedUser;
try {
  storedUser = JSON.parse(localStorage.getItem("user") as string);
} catch (e) {
  storedUser = false;
}

const initialUserState = {
  id: 0,
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
};
type UserState = typeof initialUserState;

const state = {
  [types.IS_LOGGED_IN]: !!storedUser,
  [types.LOGGING_IN]: false,
  [types.REQUESTING_RESET]: false,
  [types.RESETTING_PASSWORD]: false,
  [types.SUBMITTING_REGISTRATION]: false,
  [types.BAD_CREDS]: false,
  [types.RESET_REQUEST_ERROR]: "",
  [types.RESET_ERROR]: "",
  [types.REGISTER_ERROR]: false,
  [types.USER]: storedUser || initialUserState,
};
type State = typeof state;

const getters = {
  [types.IS_LOGGED_IN]: (state: State) => {
    return state[types.IS_LOGGED_IN];
  },
  [types.REQUESTING_RESET]: (state: State) => {
    return state[types.REQUESTING_RESET];
  },
  [types.RESETTING_PASSWORD]: (state: State) => {
    return state[types.RESETTING_PASSWORD];
  },
  [types.USER]: (state: State) => {
    return state[types.USER];
  },
  [types.BAD_CREDS]: (state: State) => {
    return state[types.BAD_CREDS];
  },
  [types.RESET_REQUEST_ERROR]: (state: State) => {
    return state[types.RESET_REQUEST_ERROR];
  },
  [types.RESET_ERROR]: (state: State) => {
    return state[types.RESET_ERROR];
  },
  [types.REGISTER_ERROR]: (state: State) => {
    return state[types.REGISTER_ERROR];
  },
  [types.LOGGING_IN]: (state: State) => {
    return state[types.LOGGING_IN];
  },
  [types.SUBMITTING_REGISTRATION]: (state: State) => {
    return state[types.SUBMITTING_REGISTRATION];
  },
};

const mutations = {
  [types.MUTATE_SET_USER_INFO]: (state: State, payload: UserState) => {
    state[types.IS_LOGGED_IN] = true;
    state[types.BAD_CREDS] = false;
    state[types.USER] = payload;
    localStorage.setItem("user", JSON.stringify(payload));
  },
  [types.MUTATE_BAD_CREDS]: (state: State, payload: boolean) => {
    state[types.BAD_CREDS] = payload;
  },
  [types.MUTATE_RESET_REQUEST_ERROR]: (state: State, payload: string) => {
    state[types.RESET_REQUEST_ERROR] = payload;
  },
  [types.MUTATE_RESET_ERROR]: (state: State, payload: string) => {
    state[types.RESET_ERROR] = payload;
  },
  [types.MUTATE_REGISTER_ERROR]: (state: State, payload: boolean) => {
    state[types.REGISTER_ERROR] = payload;
  },
  [types.MUTATE_SUBMITTING_REGISTRATION]: (state: State, payload: boolean) => {
    if (payload) state[types.REGISTER_ERROR] = false;
    state[types.SUBMITTING_REGISTRATION] = payload;
  },
  [types.MUTATE_LOGGING_IN]: (state: State, payload: boolean) => {
    if (payload) state[types.BAD_CREDS] = false;
    state[types.LOGGING_IN] = payload;
  },
  [types.MUTATE_REQUESTING_RESET]: (state: State, payload: boolean) => {
    state[types.REQUESTING_RESET] = payload;
  },
  [types.MUTATE_RESETTING_PASSWORD]: (state: State, payload: boolean) => {
    state[types.RESETTING_PASSWORD] = payload;
  },
  [types.MUTATE_LOGOUT]: (state: State) => {
    state[types.IS_LOGGED_IN] = false;
    state[types.USER] = initialUserState;
  },
};

const actions = {
  [types.GET_CURRENT_USER]: ({ commit }: { commit: Commit }) => {
    apiService.getCurrentUser().then((res) => {
      if (!res) return;

      commit(types.MUTATE_SET_USER_INFO, res);
    });
  },
  [types.LOGIN]: (
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch; state?: State },
    payload: { email: string; password: string }
  ) => {
    commit(types.MUTATE_LOGGING_IN, true);
    apiService
      .login(payload.email, payload.password)
      .then((res) => {
        if (!res) return;

        commit(types.MUTATE_BAD_CREDS, false);
        commit(types.MUTATE_SET_USER_INFO, res);
        dispatch(types.LIST_USER_APPLICATIONS, {
          // @ts-ignore
          userId: res.id,
        });
      })
      .catch((err) => {
        commit(
          types.MUTATE_BAD_CREDS,
          err
            ? err
            : "Sorry, something went wrong. Please contact us for assistance."
        );
      })
      .finally(() => {
        commit(types.MUTATE_LOGGING_IN, false);
      });
  },
  [types.REQUEST_PASSWORD_RESET]: (
    { commit }: { commit: Commit },
    payload: { email: string }
  ) => {
    commit(types.MUTATE_REQUESTING_RESET, true);
    return apiService
      .requestPasswordReset(payload)
      .then(() => {
        commit(types.MUTATE_RESET_REQUEST_ERROR, false);
      })
      .catch(() => {
        commit(types.MUTATE_RESET_REQUEST_ERROR, true);
      })
      .finally(() => {
        commit(types.MUTATE_REQUESTING_RESET, false);
      });
  },
  [types.RESET_PASSWORD]: (
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    payload: { email: string; password: string; token: string }
  ) => {
    commit(types.MUTATE_RESETTING_PASSWORD, true);
    apiService
      .resetPassword(payload)
      .then(() => {
        commit(types.MUTATE_RESET_ERROR, false);

        // reset succesful! Login:
        dispatch(types.LOGIN, {
          email: payload.email,
          password: payload.password,
        });
      })
      .catch((err) => {
        commit(
          types.MUTATE_RESET_ERROR,
          err
            ? err
            : "Sorry, something went wrong. Please contact us for assistance."
        );
      })
      .finally(() => {
        commit(types.MUTATE_RESETTING_PASSWORD, false);
      });
  },
  [types.REGISTER]: (
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    payload: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      password: string;
    }
  ) => {
    commit(types.MUTATE_SUBMITTING_REGISTRATION, true);
    apiService
      .register(payload)
      .then(() => {
        commit(types.MUTATE_REGISTER_ERROR, false);

        // register succesful! Login:
        dispatch(types.LOGIN, {
          email: payload.email,
          password: payload.password,
        });
      })

      .catch((err) => {
        commit(
          types.MUTATE_REGISTER_ERROR,
          err
            ? err
            : "Sorry, something went wrong. Please contact us for assistance."
        );
      })
      .finally(() => {
        commit(types.MUTATE_SUBMITTING_REGISTRATION, false);
      });
  },
  [types.LOGOUT]: ({ commit }: { commit: Commit }) => {
    commit(types.MUTATE_LOGGING_IN, true);
    apiService
      .logout()
      .then(() => {
        // Remove user from local storage
        localStorage.removeItem("user");
        commit(types.MUTATE_LOGOUT);

        if (router.currentRoute.name != "sign-in")
          router.push({
            path: "/sign-in",
          });
      })
      .finally(() => {
        commit(types.MUTATE_LOGGING_IN, false);
      });
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};
